// Shared helpers + primitives for the Single-Location Command Center.
// Visual language: "Tactical Precision" — dense, 1px borders, mono numerals,
// amber accent. Deliberately distinct from the multi-location dashboard.
import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";

export const money = (v) => {
  const n = Number(v || 0);
  return "$" + n.toLocaleString(undefined, Number.isInteger(n) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const moneySigned = (v) => (Number(v || 0) < 0 ? "-" : "") + money(Math.abs(Number(v || 0)));

export const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":");
  const hh = parseInt(h, 10);
  if (isNaN(hh)) return t;
  return `${((hh + 11) % 12) + 1}:${m || "00"} ${hh >= 12 ? "PM" : "AM"}`;
};

export const timeAgo = (ts) => {
  if (!ts) return "";
  const d = new Date(String(ts));
  if (isNaN(d)) return "";
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const getAuthCfg = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export const STATUS_TONE = {
  scheduled: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
  pending: "bg-slate-500/10 text-slate-500",
  in_progress: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  deposit_collected: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
  completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  completed_pending_payment: "bg-orange-500/10 text-orange-500",
  cancelled: "bg-rose-500/10 text-rose-500",
};
export const statusLabel = (s) => (s || "").replace(/_/g, " ");

export const Overline = ({ children, className = "" }) => (
  <span className={`text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground ${className}`}>{children}</span>
);

// Dense bordered panel — the base container of the command center.
export const Panel = ({ title, icon: Icon, right, children, testid, className = "", bodyClass = "" }) => (
  <section className={`rounded-md border border-border bg-card ${className}`} data-testid={testid}>
    {(title || right) && (
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 px-3.5 pt-3 pb-2 border-b border-border/60">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />}
          <Overline className="text-foreground/70">{title}</Overline>
        </div>
        {right}
      </div>
    )}
    <div className={`px-3.5 py-3 ${bodyClass}`}>{children}</div>
  </section>
);

// Trend chip. invert=true flips colors (e.g. expenses: down = good).
export const TrendChip = ({ pct, invert = false, suffix = "vs prev." }) => {
  if (pct === null || pct === undefined) return <span className="text-[10px] text-muted-foreground/50 font-mono">—</span>;
  const up = pct >= 0;
  const good = invert ? !up : up;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold font-mono ${good ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-500"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(pct)}% <span className="text-muted-foreground/60 font-normal font-sans">{suffix}</span>
    </span>
  );
};

export const InlineError = ({ label, onRetry }) => (
  <div className="text-center py-5">
    <p className="text-xs text-muted-foreground mb-1.5">Unable to load {label}</p>
    {onRetry && <button onClick={onRetry} className="text-xs text-amber-600 dark:text-amber-400 hover:underline font-medium">Retry</button>}
  </div>
);

export const Sk = ({ className = "h-4 w-20" }) => (
  <div className={`rounded bg-muted animate-pulse ${className}`} />
);

export const CHART_COLORS = ["#f59e0b", "#4f46e5", "#10b981", "#e11d48", "#06b6d4", "#8b5cf6", "#64748b", "#f97316"];
